/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef} from 'react';
import Account from "../../../models/accountModel";
import {AccountStatus} from "../../../util/interfaces";
import {AppContext} from "../../misc/appContext";
import {onPingStatusEvent} from "../../../util/events";
import {deleteFromLocal} from "../../../util/storageUtil";

const PING_TIMEOUT = 60000; // Every 1 minute
const IMPORT_TIMEOUT = 10000; // Check every 10 seconds when status is importing

let currentStatus: AccountStatus | null = null, currentError: string = '';
export const getCurrentStatus = (): AccountStatus | null => currentStatus;
export const getCurrentError = (): string => currentError;

export default function AccountStatusChecker() {
    const context = useContext(AppContext);
    const checkTimeout = useRef<number | null>(null);
    const abortController = useRef<AbortController | null>(null);
    const didInitialPing = useRef(false);

    function check() {
        abortPrevious();
        clearTimeout();
        const {account} = context.accountContext;
        if (!account) return;
        abortController.current = account.pingStatus((account: Account) => {
            abortController.current = null;
            currentStatus = account.status;
            currentError = account.error;
            onPingStatusEvent();
            context.onChangeContext({account});
            let checkTime: number;
            if (account.status === AccountStatus.IMPORTING) {
                checkTime = IMPORT_TIMEOUT;
            } else {
                deleteFromLocal('send_history_from');
                checkTime = PING_TIMEOUT;
            }
            checkTimeout.current = window.setTimeout(check, checkTime);
            didInitialPing.current = true;
        });
    }

    function abortPrevious() {
        if (abortController.current) abortController.current.abort();
    }

    function clearTimeout() {
        if (checkTimeout.current !== null) {
            window.clearTimeout(checkTimeout.current);
            checkTimeout.current = null;
        }
    }

    useEffect(() => {
        check();
        return function () {
            abortPrevious();
            clearTimeout();
        }
    }, [context.accountContext.account]);

    return null;
}